// SpeedoTube Centralized API & Server Configuration

// njsapitube.speedoprime.com — the primary real-content backend
// Contains real content: Spirituality, AI, Coding, Food, Action, Adventure, Animation categories
export const USE_LIVE_BACKEND = true;

export const LIVE_BACKEND_HOST = 'https://njsapitube.speedoprime.com';
export const LOCAL_BACKEND_URL = 'http://localhost:4572';

// Dynamic API Base URL resolver:
// When running in dev mode on localhost:3000, return '' (relative URL) so that
// requests route through setupProxy.js (http-proxy-middleware) to https://njsapitube.speedoprime.com
// This completely avoids browser CORS preflight blocks.
// In production builds, call https://njsapitube.speedoprime.com directly or via web proxy.
export const API_BASE_URL = (() => {
  if (typeof window !== 'undefined') {
    // Clear ALL stale local storage overrides that might point to old backends
    const override = localStorage.getItem('speedotube_api_base_url');
    if (override) {
      if (
        override.includes('trycloudflare.com') ||
        override.includes('103.88.81.') ||
        override === LOCAL_BACKEND_URL ||
        override === 'http://localhost:4572' ||
        override === 'http://127.0.0.1:4572'
      ) {
        localStorage.removeItem('speedotube_api_base_url');
      } else {
        return override;
      }
    }

    const forceLive = localStorage.getItem('speedotube_use_live_backend');
    if (forceLive === 'false') {
      localStorage.removeItem('speedotube_use_live_backend');
    }

    // If hosted in a browser (localhost dev, test.speedotube.com, speedotube.com),
    // use relative URL ('') so requests go to the same origin (/api/...) and get proxied
    // by either setupProxy.js (locally) or proxy.php / .htaccess (on cPanel).
    // This provides 100% immune protection against browser CORS cross-origin blocks!
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      // In all web browser contexts, use same-origin relative URLs
      return '';
    }
  }

  return LIVE_BACKEND_HOST;
})();

export const LIVE_BACKEND_URL = API_BASE_URL;

// Normalizes media URLs (thumbnails, HLS streams, MP4, banners, avatars) so they always point to the
// active njsapitube.speedoprime.com backend.
// - In localhost development, all media URLs are converted to relative paths (e.g. /drive/...)
//   so they route through CRA setupProxy.js, which guarantees NO CORS blocking by the browser!
export function normalizeMediaUrl(url) {
  if (!url || typeof url !== 'string') return url;
  let normalized = url.trim();

  // If already a base64 data URI or blob, return as is
  if (normalized.startsWith('data:') || normalized.startsWith('blob:')) {
    return normalized;
  }

  const isBrowser = typeof window !== 'undefined';
  const isLocalDev =
    isBrowser &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // In all browser contexts (localhost, test.speedotube.com, speedotube.com),
  // strip the remote njsapitube host so HLS m3u8 manifests and ts chunks route
  // via same-origin relative URLs (/drive/...) through the web server proxy.
  // This completely eliminates CORS XMLHttpRequest blocks in HLS.js!
  if (isBrowser && /^https?:\/\/njsapitube\.speedoprime\.com/i.test(normalized)) {
    normalized = normalized.replace(/^https?:\/\/njsapitube\.speedoprime\.com/i, '');
  }

  // Strip old trycloudflare hosts (any subdomain)
  normalized = normalized.replace(/^https?:\/\/[a-z0-9-]+\.trycloudflare\.com/i, '');

  // Strip old IP-based or localhost backends (with any port)
  normalized = normalized.replace(
    /^https?:\/\/(?:[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}|localhost|127\.0\.0\.1)(?::\d+)?/i,
    ''
  );

  // If on local dev and path starts with /, keep it relative!
  if (isLocalDev && normalized.startsWith('/')) {
    if (normalized.includes(' ')) {
      normalized = normalized.replace(/ /g, '%20');
    }
    return normalized;
  }

  // Ensure a single leading slash for relative paths
  if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
    if (!normalized.startsWith('/')) {
      normalized = `/${normalized}`;
    }
  }

  // If path is relative (/drive/...), keep it relative in browser so it proxies cleanly!
  if (normalized.startsWith('/')) {
    if (typeof window !== 'undefined') {
      if (normalized.includes(' ')) {
        normalized = normalized.replace(/ /g, '%20');
      }
      return normalized;
    }
    normalized = `${LIVE_BACKEND_HOST}${normalized}`;
  }

  // Upgrade any http:// to https://
  if (normalized.startsWith('http://njsapitube.speedoprime.com')) {
    normalized = normalized.replace('http://', 'https://');
  }

  if (normalized.includes(' ')) {
    normalized = normalized.replace(/ /g, '%20');
  }

  return normalized;
}

export function getFullApiUrl(path) {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${cleanPath}`;
}

export default {
  USE_LIVE_BACKEND,
  LIVE_BACKEND_URL,
  LOCAL_BACKEND_URL,
  API_BASE_URL,
  normalizeMediaUrl,
  getFullApiUrl,
};
